/**
 * Client-side permission migration.
 *
 * Bridges the gap between newly-added pages (registered in pages.json) and the
 * permissions actually stored against existing roles in the backend. Without this
 * layer, every new page would require an admin to manually edit every role and
 * every user to re-login before the sidebar entry appears — even when the new
 * page is the obvious successor of a permission the role already has.
 *
 * Pattern mirrors arise-admin's permission-migration.ts.
 */
export function migratePermissions(permissions: any): any {
  if (!permissions || typeof permissions !== 'object') return permissions;

  const migrated = { ...permissions };

  // Auto-grant "Test Upload" to roles that already have content-creation access.
  // The AI-assisted test-upload feature is the natural successor of the manual
  // test-creation flow — anyone who can build a Test or work in the Question
  // Bank should be able to use it without a manual role-edit + re-login cycle.
  // Roles that explicitly should NOT see Test Upload can be edited in the role
  // UI to drop it — this migration only ADDS when missing.
  if (
    !migrated['Test Upload'] &&
    (migrated['Test'] || migrated['Question Bank'] || migrated['Admin Panel'])
  ) {
    migrated['Test Upload'] = ['Upload Test', 'Review & Approve'];
  }

  // Auto-grant "Media" to roles that already author or review question content.
  // Without this the Media Library is unreachable for EVERYONE on the day it ships,
  // including admins: hasPermission() below has no admin fallback (unlike the
  // server's requirePermission in MediaService), so a role whose stored JSON has no
  // "Media" key fails the PermissionRoute check and lands on /app/forbidden.
  //
  // Uploading is granted alongside viewing because the two are inseparable in
  // practice - the library exists so question authors can put a clip in a question.
  // "Delete Media" is deliberately NOT auto-granted: destroying a file that live
  // questions may embed is irreversible and should be an explicit decision, not
  // something inherited. MediaService.requirePermission excludes delete from its
  // matching fallback for the same reason - the two must agree, or the UI would
  // offer a Delete button that the server then refuses.
  // This migration only ADDS when the category is missing entirely.
  if (
    !migrated['Media'] &&
    (migrated['Dashboard'] || migrated['Question Bank'] || migrated['Test'] ||
      migrated['QA'] || migrated['Admin Panel'])
  ) {
    migrated['Media'] = ['View Media', 'Upload Media'];
  }

  // Auto-grant the plain "Delete Question" action to roles that can already delete today.
  //
  // Before this category existed, the Question Repo delete icon was gated on
  // checkPermission('Question Repo', 'Delete') - and because "Question Repo" carries an EMPTY
  // sub-permission list, hasPermission() treats it as full access, so having the page was having
  // the delete. The Dashboard's bulk delete is gated on its own "Delete" action and calls the same
  // endpoint. Without this migration both would go dark on deploy for every existing role, since
  // no stored role JSON has the new category.
  //
  // "Force Delete Question" is deliberately NOT auto-granted. It strips a question out of a
  // published test or a module students are part-way through, which is not something anyone should
  // inherit from having had the page yesterday - it has to be an explicit grant.
  // requireDeletePermission() in QuestionBankApp's QuestionBankService mirrors this exactly,
  // including the force exclusion. KEEP THE TWO IN SYNC, or the UI shows a button the server 403s.
  if (
    !migrated['Question Repo Actions'] &&
    (migrated['Question Repo'] ||
      (Array.isArray(migrated['Dashboard']) && migrated['Dashboard'].indexOf('Delete') > -1) ||
      // An empty Dashboard array means full access to the category, "Delete" included.
      (Array.isArray(migrated['Dashboard']) && migrated['Dashboard'].length === 0))
  ) {
    migrated['Question Repo Actions'] = ['Delete Question'];
  }

  return migrated;
}

/**
 * Get migrated permissions from localStorage.
 * Use this in components instead of reading localStorage directly.
 */
export function getMigratedPermissions(): any {
  try {
    const permissions = JSON.parse(localStorage.getItem('permissions') || '{}');
    return migratePermissions(permissions);
  } catch (e) {
    return {};
  }
}
